/* eslint-disable @typescript-eslint/no-explicit-any */
import { observable, configure, flow, action } from 'mobx'
import API from '@aws-amplify/api'
import _ from 'lodash'

import { ToastType, ToastTypeEnum } from '../components/Dashboard/Toasts'

configure({ enforceActions: 'always' })

export interface RelatedProspect {
  email: string
  'REMOVE FROM MAVERICK': string
  'RESUME MANEUVER': string
  'SEND TO MARKETING': string
  'MANEUVER STATUS': string
  leadId: number
  name: string
  firstName: string
  lastName: string
  title: string
  Maneuver: string
  STATUS: string
  'SEND DATE': string
  DNC: string
  addedBy: string
  company: string
  domain: string
  phoneNumber: any
}

export class Related {
  @observable toasts: ToastType[] = []
  @observable isLoading = false
  @observable relatedProspects: RelatedProspect[] = []
  @observable currentSearch = ''
  @observable cacheValid = false

  @action
  pushToToasts(toast: ToastType): void {
    const newToast = toast

    if (newToast.show === undefined) {
      newToast.show = true
    }

    if (newToast.delay === undefined) {
      newToast.delay = 3000
    }

    this.setToasts(this.toasts.concat(newToast))
  }

  @action
  popFromToasts(): void {
    const newToasts = _.cloneDeep(this.toasts)

    newToasts.shift()
    this.setToasts(newToasts)
  }

  @action
  setToasts(toasts: ToastType[]): void {
    this.toasts = toasts
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  @action
  setRelatedProspects(relatedProspects: RelatedProspect[]): void {
    this.relatedProspects = relatedProspects
  }

  @action
  setCurrentSearch(currentSearch: string): void {
    this.currentSearch = currentSearch
  }

  @action
  setCacheValid(cacheValid: boolean): void {
    this.cacheValid = cacheValid
  }

  @action
  getRelatedProspects = flow(function*(this: Related, email: string) {
    this.setIsLoading(true)
    this.setCurrentSearch(email)

    const emailDomain = email.substring(email.lastIndexOf('@') + 1)

    try {
      const response = yield API.get('mav-outlook-api', `/leads/search?company=${emailDomain}`, {})
      // Filter out matches that have the same email as the searched email
      const relatedProspects = response.matches.filter(
        (prospect: RelatedProspect) => prospect.email !== email,
      )
      // Sort related prospects by last name in ascending order
      const sortedRelatedProspects = _.orderBy(relatedProspects, ['lastName'], ['asc'])

      this.setIsLoading(false)
      this.setCacheValid(true)
      this.setRelatedProspects(sortedRelatedProspects)
    } catch (e) {
      // console.error(e)

      if (_.get(e, 'response.status') !== 404) {
        this.pushToToasts({
          type: ToastTypeEnum.ERROR,
          message: 'Unable to get related prospects',
        })
      }

      this.setCurrentSearch('')
      this.setCacheValid(false)
      this.setIsLoading(false)
    }
  })

  @action
  sendToMarketingChange = flow(function*(this: Related, email: string, checked: boolean) {
    const targetProspectIndex = _.findIndex(this.relatedProspects, ['email', email])
    if (targetProspectIndex < 0) return
    const targetProspect = this.relatedProspects[targetProspectIndex]
    const initialTargetProspectChecked = targetProspect['SEND TO MARKETING']
    const newRelatedProspects = _.cloneDeep(this.relatedProspects)

    if (checked) {
      targetProspect['SEND TO MARKETING'] = 'true'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    } else {
      targetProspect['SEND TO MARKETING'] = 'false'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    }

    const payload = {
      sendToMarketing: checked,
      leadId: targetProspect.leadId,
    }

    const myInit = { body: payload }

    try {
      yield API.put('mav-outlook-api', `/leads/${email}`, myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify marketing setting",
      })

      targetProspect['SEND TO MARKETING'] = initialTargetProspectChecked
      newRelatedProspects[targetProspectIndex] = targetProspect
      this.setRelatedProspects(newRelatedProspects)
    }
  })

  @action
  activateManeuverChange = flow(function*(this: Related, email: string, checked: boolean) {
    const targetProspectIndex = _.findIndex(this.relatedProspects, ['email', email])
    const targetProspect = this.relatedProspects[targetProspectIndex]
    const initialTargetProspectChecked = targetProspect.STATUS
    const newRelatedProspects = _.cloneDeep(this.relatedProspects)

    if (checked) {
      targetProspect.STATUS = 'activated'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    } else {
      targetProspect.STATUS = 'Waiting...'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    }

    const payload = {
      deactivate: checked,
      leadId: targetProspect.leadId,
    }

    const myInit = { body: payload }

    try {
      yield API.put('mav-outlook-api', `/leads/${email}`, myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify maneuver setting",
      })

      targetProspect.STATUS = initialTargetProspectChecked
      newRelatedProspects[targetProspectIndex] = targetProspect
      this.setRelatedProspects(newRelatedProspects)
    }
  })

  @action
  prospectDNCChange = flow(function*(this: Related, email: string, checked: boolean) {
    const targetProspectIndex = _.findIndex(this.relatedProspects, ['email', email])
    const targetProspect = this.relatedProspects[targetProspectIndex]
    const initialTargetProspectChecked = targetProspect.DNC
    const newRelatedProspects = _.cloneDeep(this.relatedProspects)

    if (checked) {
      targetProspect.DNC = 'true'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    } else {
      targetProspect.DNC = 'false'
      newRelatedProspects[targetProspectIndex] = targetProspect

      this.setRelatedProspects(newRelatedProspects)
    }

    const payload = {
      dnc: checked,
      leadId: targetProspect.leadId,
    }

    const myInit = { body: payload }

    try {
      yield API.put('mav-outlook-api', `/leads/${email}`, myInit)
    } catch (e) {
      // console.error(e)

      this.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: "Couldn't modify DNC list setting",
      })

      targetProspect.DNC = initialTargetProspectChecked
      newRelatedProspects[targetProspectIndex] = targetProspect
      this.setRelatedProspects(newRelatedProspects)
    }
  })

  @action
  clearState(): void {
    this.setToasts([])
    this.setIsLoading(false)
    this.setRelatedProspects([])
    this.setCurrentSearch('')
    this.setCacheValid(false)
  }
}

export default new Related()
